import React, { useState, useEffect, useRef, useCallback } from "react";
import queueApi from "../services/api";
import signalRService from "../services/signalR";
import { useLanguage } from "../contexts/LanguageContext";
import LanguageToggle from "./LanguageToggle";
import "./QueueDisplay.css";
import photo_logo from "../images/icon.svg";
import imageGroup from "../images/groups.svg";
import imageNotification from "../images/ion_notifcations.svg";
import iconEmployer from "../images/employer.svg";
import iconHeart from "../images/premium.svg";
import iconStar from "../images/vip.svg";
import iconCrown from "../images/vip+.svg";
import "./Skeleton.css";
import { parseDateInTajikistan, formatTajikistanTime, formatTajikistanDate } from "../utils/time";

const DynamicIcon = ({ typeId, icon, isCall }) => {
  if (!icon) return null;
  const specificClass = isCall ? `icon-call-${typeId}` : `icon-wait-${typeId}`;
  return React.cloneElement(icon, { className: `icon-img ${specificClass}` });
};

const Icons = {
  Employer: <img src={iconEmployer} alt="employer" />,
  Heart: <img src={iconHeart} alt="heart" />,
  Star: <img src={iconStar} alt="star" />,
  Crown: <img src={iconCrown} alt="crown" />
};

const DisplaySkeleton = () => (
    <div className="hall-container">
      <header className="hall-header">
        <div className="skeleton-base" style={{width: '400px', height: '60px'}}></div>
        <div className="skeleton-base" style={{width: '250px', height: '60px'}}></div>
      </header>
      <main className="hall-content">
        <div className="hall-column">
          <div className="skeleton-base" style={{width: '200px', height: '40px', marginBottom: '20px'}}></div>
          {[1, 2, 3, 4, 5, 6,7,8,9].map(i => <div key={i} className="skeleton-base" style={{height: '82px', marginBottom: '12px', borderRadius: '24px'}}></div>)}
        </div>
        <div className="hall-divider"></div>
        <div className="hall-column">
          <div className="skeleton-base" style={{width: '200px', height: '40px', marginBottom: '20px'}}></div>
          {[1, 2, 3,4,5,6].map(i => <div key={i} className="skeleton-base" style={{height: '124px', marginBottom: '16px', borderRadius: '24px'}}></div>)}
        </div>
      </main>
    </div>
);
const QueueDisplay = () => {
  const { getText } = useLanguage();

  const [currentTime, setCurrentTime] = useState(new Date());
  const [queueData, setQueueData] = useState({ called: [], waiting: [], countWaiting: 0, countCalled: 0 });
  const [blinkingIds, setBlinkingIds] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const mountedRef = useRef(true);
  const [showModal, setShowModal] = useState(false);
  const [clickCount, setClickCount] = useState(0);
  const [token, setToken] = useState('');
  const [modalError, setModalError] = useState('');
  const [modalLoading, setModalLoading] = useState(false);

  const calculateElapsedTime = (dateCreated) => {
    if (!dateCreated) return "00:00";
    const start = parseDateInTajikistan(dateCreated).getTime();
    const now = currentTime.getTime();
    const diffInSeconds = Math.floor((now - start) / 1000);
    if (diffInSeconds < 0) return "00:00";
    const minutes = Math.floor(diffInSeconds / 60);
    const seconds = diffInSeconds % 60;
    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  };

  const triggerBlink = useCallback((id) => {
    setBlinkingIds(prev => new Set(prev).add(id));
    setTimeout(() => {
      if (mountedRef.current) {
        setBlinkingIds(prev => {
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
      }
    }, 8000);
  }, []);

  const updateQueueState = useCallback((view) => {
    if (!view) return;

    // Cache client type names from queue data
    const allQueueItems = [...(view.called || []), ...(view.waiting || [])];
    allQueueItems.forEach(item => {
      if (item.clientTypeId && item.clientTypeName) {
        queueApi.updateClientTypeCacheFromQueue(item);
      }
    });

    setQueueData({
      called: Array.isArray(view.called) ? view.called.slice(0, 6) : [],
      waiting: Array.isArray(view.waiting) ? view.waiting.slice(0, 9) : [],
      countWaiting: view.countWaiting || 0,
      countCalled: view.countCalled || 0
    });

    const now = new Date().getTime();
    view.called?.forEach(item => {
      if (item.update) {
        const updateTime = new Date(item.update).getTime();
        const diff = (now - updateTime) / 1000;
        if (diff >= 0 && diff <= 8) triggerBlink(item.id);
      }
    });
  }, [triggerBlink]);

  useEffect(() => {
    if (clickCount >= 3) {
      setShowModal(true);
      setClickCount(0);
    }
  }, [clickCount]);

  const handleLogoClick = () => {
    setClickCount(prev => prev + 1);
  };

  const handleModalSubmit = async (e) => {
    e.preventDefault();
    setModalLoading(true);
    setModalError('');
    try {
      queueApi.setToken(token);
      const response = await queueApi.getServices();
      if (response) {
        setShowModal(false);
        window.location.reload();
      } else {
        setModalError('Токен нодуруст аст ё сервер дастрас нест. Консолро тафтиш кунед.');
      }
    } catch (err) {
      console.error('Token validation error:', err);
      setModalError('Хатои пайвастшавӣ ба сервер. Шабака ва URL-ро санҷед.');
    } finally {
      setModalLoading(false);
    }
  };

  useEffect(() => {
    const unsubscribe = signalRService.onQueueView(view => {
      if (view && mountedRef.current) updateQueueState(view);
    });

    const loadData = async () => {
      try {
        await signalRService.refreshQueue();
      } catch (err) {
        console.error("Хатои боркунӣ:", err);
      } finally {
        if (mountedRef.current) setLoading(false);
      }
    };

    loadData();

    return () => {
      mountedRef.current = false;
      unsubscribe();
    };
  }, [updateQueueState]);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    let wasOffline = false;

    const handleOffline = () => {
      wasOffline = true;
    };

    const handleOnline = () => {
      if (wasOffline) {
        wasOffline = false;
        window.location.reload();
      }
    };

    window.addEventListener('offline', handleOffline);
    window.addEventListener('online', handleOnline);

    return () => {
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('online', handleOnline);
    };
  }, []);

  const getClientConfig = () => {
    const getCachedClientLabel = (clientTypeId) => {
      const cachedName = queueApi.getClientTypeFromCache(clientTypeId);
      if (cachedName) {
        return cachedName;
      }
      return `Новий`;
    };

    const getClientIcon = (clientTypeId) => {
      const iconMap = {
        2: Icons.Employer,
        3: Icons.Heart,
        4: Icons.Star,
        5: Icons.Crown
      };
      return iconMap[clientTypeId] || null;
    };

    const getClientClasses = (clientTypeId) => {
      const classMap = {
        1: { waitClass: "wait-default", callClass: "call-default" },
        2: { waitClass: "wait-employer", callClass: "call-employer" },
        3: { waitClass: "wait-premium", callClass: "call-premium" },
        4: { waitClass: "wait-vip", callClass: "call-vip" },
        5: { waitClass: "wait-vip-plus", callClass: "call-vip-plus" }
      };
      return classMap[clientTypeId] || classMap[1];
    };

    return (clientTypeId) => {
      const classes = getClientClasses(clientTypeId);
      return {
        id: clientTypeId,
        label: getCachedClientLabel(clientTypeId),
        icon: getClientIcon(clientTypeId),
        waitClass: classes.waitClass,
        callClass: classes.callClass
      };
    };
  };

  const dynamicClientConfig = getClientConfig();

  const textLabels = {
    electronic: getText('ЭЛЕКТРОННАЯ ', 'НАВБАТИ '),
    queue: getText('ОЧЕРЕДЬ', 'ЭЛЕКТРОНӢ'),
    infoBoard: getText('ИНФОРМАЦИОННОЕ ТАБЛО', 'ЛАВҲАИ МАЪЛУМОТ'),
    inQueue: getText('В ОЧЕРЕДИ', 'ДАР НАВБАТ'),
    goToWindow: getText('ПРОЙДИТЕ К ОКНУ', 'БА МИЗ ГУЗАРЕД'),
    window: getText('окно', ''),
    noOneInQueue: getText('В очереди никого нет', 'Дар навбат ҳеҷ кас нест'),
    waitForInvitation: getText('Ожидайте приглашения', 'Интизори даъват бошед')
  };

  if (loading) {
    return <DisplaySkeleton />;
  }

  return (
      <div className="hall-container">
        <header className="hall-header">
          <div className="farmoish-logo" onClick={handleLogoClick}>
            <img src={photo_logo} alt="logo" className="hall-logo-img" />
            <div className="logo-text-block">
              <h1 className="logo-main-title">{textLabels.electronic} <span className="bold">{textLabels.queue}</span></h1>
              <p className="logo-subtitle">{textLabels.infoBoard}</p>
            </div>
          </div>
            <div className="hall-clock-wrapper">
              <div className="hall-clock">{formatTajikistanTime(currentTime)}</div>
              <div className="hall-date">{formatTajikistanDate(currentTime)}</div>
          </div>
        </header>

        <main className="hall-content">
          <section className="hall-column">
            <div className="column-head">
              <h2>{textLabels.inQueue}</h2>
              <span className="queue-count">{queueData.countWaiting}</span>
            </div>
            <div className="queue-list">
              {queueData.waiting.length > 0 ? (
                  queueData.waiting.map((item, i) => {
                    const stableCache = signalRService.getStableCache();
                    const cachedTicket = stableCache.tickets?.[item.id];
                    const effectiveClientTypeId = item.clientTypeId || cachedTicket?.clientTypeId || 1;
                    
                    const config = dynamicClientConfig(effectiveClientTypeId);
                    return (
                        <div key={`wait-${item.id || i}`} className={`hall-card-waiting ${config.waitClass}`}>
                          <div className="card-inner-left">
                            {config.icon && (
                                <div className="icon-badge">
                                  <DynamicIcon typeId={config.id} icon={config.icon} isCall={false} />
                                </div>
                            )}
                            <div className="client-info">
                              <span className="client-label">{config.label}</span>
                              <span className="client-code">{item.code}</span>
                            </div>
                          </div>
                          <div className="wait-timer">{calculateElapsedTime(item.dateCreated)}</div>
                        </div>
                    );
                  })
              ) : (
                  <div className="empty-state">
                    <img src={imageGroup} alt="No one" className="empty-img-groups" />
                    <p className="empty-text">{textLabels.noOneInQueue}</p>
                  </div>
              )}
            </div>
          </section>

          <div className="hall-divider"></div>

          <section className="hall-column">
            <div className="column-head">
              <h2 className="purple-title">{textLabels.goToWindow}</h2>
            </div>
            <div className="called-list">
              {queueData.called.length > 0 ? (
                  queueData.called.map((item, i) => {
                    // Get cached data for clientTypeId fallback
                    const stableCache = signalRService.getStableCache();
                    const cachedTicket = stableCache.tickets?.[item.id];
                    const effectiveClientTypeId = item.clientTypeId || cachedTicket?.clientTypeId || 1;
                    
                    const config = dynamicClientConfig(effectiveClientTypeId);
                    const isBlinking = blinkingIds.has(item.id);
                    return (
                        <div
                            key={`call-${item.id || i}`}
                            className={`hall-card-called ${isBlinking ? 'is-new-blinking' : config.callClass}`}
                        >
                          <div className="card-inner-left">
                            {config.icon && (
                                <div className="icon-badge-large">
                                  <DynamicIcon typeId={config.id} icon={config.icon} isCall={true} />
                                </div>
                            )}
                            <div className="client-info">
                              <span className="client-label">{config.label}</span>
                              <span className="client-code-large">{item.code}</span>
                            </div>
                          </div>
                          <div className="window-block">
                            <span className="win-text">{textLabels.window}</span>
                            <span className="win-number">
                              {(item.isPhoneNumber === true)
                                  ? `хазинаи ${item.windowNumber}` 
                                  : `машваратчӣ ${item.windowNumber}`
                              }
                            </span>
                          </div>
                        </div>
                    );
                  })
              ) : (
                  <div className="empty-state">
                    <img src={imageNotification} alt="Wait" className="empty-img-notif" />
                    <p className="empty-text">{textLabels.waitForInvitation}</p>
                  </div>
              )}
            </div>
          </section>
        </main>

        {showModal && (
            <div className="modal-overlay" onClick={() => setShowModal(false)}>
              <div className="modal-content" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                  <h2>{getText('API Key Configuration', 'Танзимоти калиди API')}</h2>
                  <button className="modal-close" onClick={() => setShowModal(false)}>&times;</button>
                </div>
                <form onSubmit={handleModalSubmit}>
                  <div className="modal-body">
                    <div className="input-group">
                      <label>{getText('Введите ваш API ключ:', 'Калиди API-и худро ворид кунед:')}</label>
                      <input
                          type="text"
                          className="input-field"
                          placeholder="API Key"
                          value={token}
                          onChange={e => setToken(e.target.value)}
                          required
                      />
                    </div>
                    {modalError && <div className="error-message">{modalError}</div>}
                  </div>
                  <div className="modal-footer">
                    <button type="button" className="button button-cancel" onClick={() => setShowModal(false)}>{getText('Отмена', 'Бекор кардан')}</button>
                    <button type="submit" className="button button-submit" disabled={modalLoading}>
                      {modalLoading ? getText('Сохранение...', 'Нигоҳ доштан...') : getText('Сохранить', 'Нигоҳ доред')}
                    </button>
                  </div>
                </form>
                
                {/*<LanguageToggle />*/}
              </div>
            </div>
        )}
      </div>
  );
};

export default QueueDisplay;